import { FC, useEffect, useState } from "react";
import { Table, Button, Space, Card } from "antd";
import { Link } from "react-router-dom";
import { PlusOutlined } from "@ant-design/icons";
import { handleError } from "../../../common/ErrorHandling";
import { getTactics, upsertTactic } from "../../../common/api/TacticClient";
import { Tactic } from "../../../types/TacticTypes";
import { useUserState } from "../../../common/UserContext";
import { isSuperAdmin } from "../../../types/UserGroup";
import { formatDateString } from "../../date/DateOp";
import { TacticEditModal } from "./components/TacticEditModal";

const { Column } = Table;

export const TacticListView: FC = () => {
    const { me } = useUserState();
    const [data, setData] = useState<Tactic[]>([]);
    const [editingRow, setEditingRow] = useState({} as Tactic);
    const [showUpdate, setShowUpdate] = useState(false);
    const [loading, setLoading]: any[] = useState(true);

    const refreshData = async () => {
        setLoading(true);
        try {
            const res = await getTactics();
            setData(res);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (record: Tactic) => {
        setEditingRow(record);
        setShowUpdate(true);
    };

    const handleAddClick = () => {
        setEditingRow({} as Tactic);
        setShowUpdate(true);
    };

    const handleUpdate = async (data: Tactic) => {
        try {
            await upsertTactic({ ...editingRow, ...data });
            await refreshData();
        } catch (error) {
            handleError(error);
        } finally {
            setShowUpdate(false);
        }
    };

    useEffect(() => {
        refreshData();
    }, []);

    const dataTable = (
        <Table size="small" dataSource={data} loading={loading} rowKey="id">
            <Column
                title="Name"
                dataIndex="name"
                key="name"
                render={(value: string, row: Tactic) => {
                    return <Link to={`/tactic/${row.id}`}>{value}</Link>;
                }}
            />
            <Column
                title="Create Date"
                dataIndex="recordCreationDate"
                key="recordCreationDate"
                render={(value: string) => {
                    return formatDateString(value);
                }}
            />
            {isSuperAdmin(me.roles || []) ? (
                <Column
                    title={
                        <Button
                            type="link"
                            size="small"
                            onClick={handleAddClick}
                            icon={<PlusOutlined />}
                        >
                            Add
                        </Button>
                    }
                    key="action"
                    render={(value, row: Tactic) => {
                        return (
                            <Space>
                                <Button
                                    type="link"
                                    onClick={() => handleEditClick(row)}
                                    size="small"
                                >
                                    Edit
                                </Button>
                            </Space>
                        );
                    }}
                />
            ) : null}
        </Table>
    );

    const panel = (
        <Card bordered={false}>
            <TacticEditModal
                title={editingRow.id ? "Update Tactic" : "Add Tactic"}
                data={editingRow}
                open={showUpdate}
                onCancel={() => setShowUpdate(false)}
                onOk={handleUpdate}
                width={1000}
            />
            {dataTable}
        </Card>
    );

    return panel;
};
